<i18n lang="yaml">
en:
  button_corners: Button corners
  card_corners: Card corners
  button_tooltip: Click to change the corner radius of buttons in the whole UI
  card_tooltip: Click to change the corner radius of cards in the whole UI
  x_small: Extra small
  small: Small
  medium: Medium
  large: Large
  x_large: Extra large
  full: Full
cs:
  button_corners: Zaoblení tlačítek
  card_corners: Zaoblení karet
  button_tooltip: Kliknutím změníte zaoblení rohů tlačítek v celé aplikaci
  card_tooltip: Kliknutím změníte zaoblení rohů karet v celé aplikaci
  x_small: Extra malé
  small: Malé
  medium: Střední
  large: Velké
  x_large: Extra velké
  full: Plné
</i18n>

<template>
  <v-menu location="bottom" min-width="320">
    <template #activator="{ props }">
      <v-tooltip location="bottom">
        <template #activator="{ props: tooltipProps }">
          <div
            class="clickable d-flex align-center text-no-wrap"
            v-bind="{ ...props, ...tooltipProps, ...$attrs }"
          >
            <div>
              <div class="input-like">{{ $t(`${kind}_corners`) }}</div>
              <div style="font-size: 14px">
                {{ $t(currentOption.key) }}
                <span class="small subdued">({{ currentOption.value }})</span>
              </div>
            </div>
            <v-icon
              icon="fas fa-caret-down"
              color="lighterIcons"
              class="pl-2"
            ></v-icon>
          </div>
        </template>
        <span>{{ $t(`${kind}_tooltip`) }}</span>
      </v-tooltip>
    </template>

    <v-list density="compact">
      <v-list-item
        v-for="option in options"
        :key="option.value"
        :active="option.value === radius"
        @click="radius = option.value"
      >
        <template #prepend>
          <span
            class="swatch mr-4"
            :class="kind"
            :style="{ borderRadius: option.value }"
          ></span>
        </template>
        <v-list-item-title>{{ $t(option.key) }}</v-list-item-title>
        <template #append>
          <span class="small subdued pl-6">{{ option.value }}</span>
        </template>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { RADII, RADIUS_OPTIONS } from "@/libs/corner-radius";

defineOptions({
  name: "CornerRadiusSelector",
  inheritAttrs: false,
});

const props = defineProps<{
  kind: "button" | "card";
}>();

const options = RADIUS_OPTIONS;
// the kind does not change during the lifetime of the component
const radius = RADII[props.kind];

const currentOption = computed(
  () => options.find((option) => option.value === radius.value) ?? options[0],
);
</script>

<style scoped lang="scss">
.small {
  font-size: 0.875rem;
}

.input-like {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.75rem;
}

.clickable {
  cursor: pointer;
  flex-shrink: 0;
}

.swatch {
  display: inline-block;
  width: 46px;
  height: 26px;
  border: 1px solid rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.12);

  &.card {
    border-color: rgb(var(--v-theme-cardBorder));
    background-color: rgb(var(--v-theme-surface));
  }
}
</style>
